import { createContext, ReactNode, useEffect, useState } from "react";
import { boundingBox, excludeIds, orgId } from "../utils/dashboardUtils";
import { BoundingBox, ItemSummary, LayerData, LayerItem } from "../services/types";
import { layersEnv } from "../utils/environment";

// Define the shape of your context data
interface LayersContextType {
    layers: LayerData[];
    items: { items: LayerItem[], summary: ItemSummary[] };
    fetchLayersData: (orgId: number, boundingBox: BoundingBox | null, excludeIds: string[] | null) => void;
}

// Create the context with default values
const LayersContext = createContext<LayersContextType>({
    layers: [],
    items: { items: [], summary: [] },
    fetchLayersData: () => {},
});

interface LayersProviderProps {
    children: ReactNode;
}

// Define the provider for the context
export const LayersProvider = ({ children }: LayersProviderProps) => {
    const [layers, setLayers] = useState<LayerData[]>([]);
    const [items, setItems] = useState<{ items: LayerItem[]; summary: ItemSummary[] }>({
        items: [],
        summary: [],
    });

    const fetchLayersData = async (orgId: number, boundingBox: BoundingBox | null, excludeIds: string[] | null) => {
        if (!orgId) {
            console.log('No orgId provided to fetch layers');
            return;
        }

        try {
            // Fetch layers
            const layersResponse = await fetch(
                `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getlayersbyorg?orgId=${orgId}&environment=${layersEnv}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!layersResponse.ok) {
                throw new Error('Failed to fetch layers');
            }

            const layersResult = await layersResponse.json();
            console.log('layers result:', layersResult);
            setLayers(layersResult.data || []);

            if (layersResult.data.length > 0) {
                const firstLayerId = layersResult.data[0].layer_id;

                // Construct boundingBox query parameters
                const boundingBoxParams = boundingBox 
                ? `&boundingBox=${encodeURIComponent(JSON.stringify(boundingBox))}` 
                : "";

                const excludeIdsParam = excludeIds ? `&excludeIds=${excludeIds.join(",")}` : "";

                // Fetch items for the first layer
                const itemsResponse = await fetch(
                    `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getlayeritems?layerId=${firstLayerId}&environment=${layersEnv}${boundingBoxParams}${excludeIdsParam}`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    }
                );

                if (!itemsResponse.ok) {
                    throw new Error('Failed to fetch layer items');
                }

                const itemsResult = await itemsResponse.json();
                setItems(itemsResult || []);
            }
        } catch (error: any) {
            console.log('Error fetching layers or items:', error.message);
            setLayers([]);
        }
    };

    useEffect(() => {
        fetchLayersData(orgId, boundingBox, excludeIds);
    }, [boundingBox, excludeIds]);

    return (
        <LayersContext.Provider value={{ layers, items, fetchLayersData }}>
            {children}
        </LayersContext.Provider>
    );
};

// Export the hook to use the context
export default LayersContext;